import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import GlobalNavigationShell from "components/v2/global-navigation-shell/GlobalNavigationShell";
import LoanCentricSidebarNav from "components/v2/loan-centric-sidebar-nav/LoanCentricSidebarNav";
import SharedNavigation, { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import useDesktopChecker from "hooks/useDesktopChecker";
import useTabletChecker from "hooks/useTabletChecker";
import { useNativeAppPromoVisibility } from "hooks/useNativeAppPromoVisibility";
import { useDispatch, useSelector } from "react-redux";
import {
  getActiveTab,
  getDealId,
  getEmailAddress,
  getEncompassLoanId,
  getLoanId,
  getLoanNumber,
  getProfileSettings,
  getProspectId,
  setProfileActiveTab,
} from "features/authSlice";
import { PalNotificationHandler } from "components/v2/notifications";
import { useNotificationContext } from "contexts/NotificationContext";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { resetScrollPosition } from "utilities/resetScrollPosition";
import {
  DASHBOARD_SESSION_ID,
  DASHBOARD_SESSION_START_TIMESTAMP,
} from "utilities/Constant";
import { trackFSDashboardPageNavigated } from "utilities/full-story";
import { getLoanOfficerPrimaryDomain } from "services/common/loanOfficerSlice";
import classNames from "classnames";
import DashboardTabContent from "../shared-layout/DashboardTabContent";
import { useDashboardShellData } from "./hooks/useDashboardShellData";

interface DashboardShellMainContentProps {
  readonly showNotifications: boolean;
  readonly onCloseNotifications: () => void;
  readonly onDocumentNotificationClick: (palInstanceId: string) => void;
  readonly tabContent: React.ReactNode;
  readonly isDesktop: boolean;
}

/** Renders inside GlobalNavigationShell so NotificationProvider is available. */
const DashboardShellMainContent: React.FC<DashboardShellMainContentProps> = ({
  showNotifications,
  onCloseNotifications,
  onDocumentNotificationClick,
  tabContent,
  isDesktop,
}) => {
  const { notifications, removeNotification } = useNotificationContext();

  const handleMobileNotificationClick = async (notificationId: string) => {
    await removeNotification(notificationId);
    onCloseNotifications();
  };

  if (!isDesktop && showNotifications) {
    return (
      <PalNotificationHandler
        notifications={notifications}
        onNotificationClick={handleMobileNotificationClick}
        onDocumentNotificationClick={onDocumentNotificationClick}
        isOpen={showNotifications}
        onClose={onCloseNotifications}
        triggerElementSelector=".global-header__notification-button"
      />
    );
  }

  return <>{tabContent}</>;
};

export const DashboardShellContent: React.FC = () => {
  const dashboardData = useDashboardShellData();
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const isMobilePhone = !isDesktop && !isTablet;
  const { isBannerVisible } = useNativeAppPromoVisibility();
  const dispatch = useDispatch();

  const reduxActiveTab = useSelector(getActiveTab);
  const showProfile = useSelector(getProfileSettings);
  const email = useSelector(getEmailAddress);
  const prospectId = useSelector(getProspectId);
  const loanNumber = useSelector(getLoanNumber);
  const encompassLoanId = useSelector(getEncompassLoanId);
  const loanId = useSelector(getLoanId);
  const dealId = useSelector(getDealId);
  const loPrimaryDomain = useSelector(getLoanOfficerPrimaryDomain);

  const [showSpinner, setShowSpinner] = useState(false);
  const [spinnerDelay, setSpinnerDelay] = useState<NodeJS.Timeout | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [pendingDocumentId, setPendingDocumentId] = useState<string | null>(null);

  const prevShowProfile = useRef(false);
  const { data: featureFlag } = useGetFeatureFlagsQuery();

  useLayoutEffect(() => {
    resetScrollPosition();
  }, [reduxActiveTab, showProfile]);

  useEffect(() => {
    if (showProfile && !prevShowProfile.current) {
      trackFSDashboardPageNavigated(getNavTrackingProps("Profile"));
    }
    prevShowProfile.current = showProfile;
  }, [showProfile]);

  const hideSpinner = () => {
    clearTimeout(spinnerDelay as NodeJS.Timeout);
    setShowSpinner(false);
  };

  const triggerSpinner = () => {
    const timeout = setTimeout(() => {
      setShowSpinner(true);
    }, 1000);
    setSpinnerDelay(timeout);
  };

  const getLoSalesDomain = (): string => {
    const domainName = loPrimaryDomain?.domainName?.toLowerCase();
    if (domainName === "retail") return "Retail";
    if (domainName === "cd") return "CD";
    return "Unassigned";
  };

  const getNavTrackingProps = (pageName: string) => ({
    email,
    prospectId,
    sessionId: sessionStorage.getItem(DASHBOARD_SESSION_ID),
    sessionStartTimestamp: sessionStorage.getItem(DASHBOARD_SESSION_START_TIMESTAMP),
    pageName,
    loanNumber: loanNumber || null,
    loanGuid: encompassLoanId || loanId || null,
    dealId: dealId || null,
    loSalesDomain: getLoSalesDomain(),
  });

  const PAGE_NAMES: Record<string, string> = {
    [NavigationIds.Home]: "Dashboard Home",
    [NavigationIds.Tasks]: "Tasks",
    [NavigationIds.Docs]: "Documents",
    [NavigationIds.LoanDetails]: "Loan Details",
  };

  const handleTabChange = (tab: string) => {
    if (tab !== NavigationIds.More) {
      triggerSpinner();
    }
    if (tab !== NavigationIds.More && PAGE_NAMES[tab]) {
      trackFSDashboardPageNavigated(getNavTrackingProps(PAGE_NAMES[tab]));
    }
    dispatch(setProfileActiveTab(tab));
  };

  const handleDocumentNotificationClick = (palInstanceId: string) => {
    if (palInstanceId) {
      setPendingDocumentId(palInstanceId);
      dispatch(setProfileActiveTab(NavigationIds.Docs));
    }
  };

  const hideFooter = reduxActiveTab === NavigationIds.More;
  const hideNotifications = reduxActiveTab === NavigationIds.More;

  const tabContent = (
    <DashboardTabContent
      activeTab={reduxActiveTab}
      dashboardData={dashboardData}
      featureFlags={featureFlag}
      pendingDocumentId={pendingDocumentId}
      onLoad={hideSpinner}
      onDocumentOpened={() => setPendingDocumentId(null)}
    />
  );

  const renderSharedNavigation = () => (
    <SharedNavigation
      activeTab={reduxActiveTab}
      onTabChange={handleTabChange}
      isDesktop={isDesktop}
      isTablet={isTablet}
      isNavOpen={false}
      setIsNavOpen={() => undefined}
    />
  );

  return (
    <div className="dashboard-shell">
      <GlobalNavigationShell
        isLoading={dashboardData.isLoading || showSpinner}
        notificationsEnabled
        shellContext="dashboard"
        shellAnalyticsContext={{
          email,
          prospectId,
          dealId,
          loanNumber: loanNumber || null,
          loanGuid: encompassLoanId || loanId || null,
        }}
        hideFooter={hideFooter}
        hideNotifications={hideNotifications}
        lockBackNavigation
        onNotificationsClick={
          !isDesktop ? () => setShowNotifications(!showNotifications) : undefined
        }
        onDocumentNotificationClick={handleDocumentNotificationClick}
        navSlot={!isMobilePhone ? <LoanCentricSidebarNav /> : undefined}
        navSlotPlacement="sidebar"
        mobileNavSlot={isMobilePhone ? renderSharedNavigation() : undefined}
        mobileContentClassName={classNames({
          "global-navigation-shell__mobile-content--banner-visible": isBannerVisible,
        })}
      >
        <DashboardShellMainContent
          showNotifications={showNotifications}
          onCloseNotifications={() => setShowNotifications(false)}
          onDocumentNotificationClick={handleDocumentNotificationClick}
          tabContent={tabContent}
          isDesktop={isDesktop}
        />
      </GlobalNavigationShell>
    </div>
  );
};

export default DashboardShellContent;
